import type { ShowCategory, ShowStatus } from "../catalog/types";
import type { FollowedShow } from "../tracking/types";

/**
 * How far into a show the reader is, as a filter rather than a percentage.
 *
 * Derived from the materialised progress counts instead of stored, because
 * "completed" has to stop being true the moment a new episode is published --
 * and a show with no episodes yet is not started, not finished.
 */
export const libraryProgressStates = ["not-started", "in-progress", "completed"] as const;
export type LibraryProgressState = (typeof libraryProgressStates)[number];

export const librarySortOrders = ["updated", "title", "progress", "year"] as const;
export type LibrarySortOrder = (typeof librarySortOrders)[number];

export const libraryCategories = ["tv", "anime", "kdrama"] as const;
export const libraryStatuses = [
  "airing",
  "upcoming",
  "returning",
  "ended",
  "cancelled",
  "unknown",
] as const;

export interface LibraryFilters {
  q?: string | undefined;
  category?: ShowCategory | undefined;
  status?: ShowStatus | undefined;
  genre?: string | undefined;
  progress?: LibraryProgressState | undefined;
}

/**
 * Case- and width-insensitive comparison key.
 *
 * NFKC first, so a title typed with half-width kana or full-width Latin matches
 * the same title stored the other way -- searching a library that deliberately
 * holds native, romanized and English titles is exactly where that bites.
 */
function foldForSearch(value: string): string {
  return value.normalize("NFKC").toLocaleLowerCase();
}

export function progressStateOf(show: FollowedShow): LibraryProgressState {
  if (show.watchedEpisodeCount <= 0) return "not-started";
  if (show.totalEligibleEpisodes > 0 && show.watchedEpisodeCount >= show.totalEligibleEpisodes) {
    return "completed";
  }
  return "in-progress";
}

/**
 * Every title a show is known by, so search reaches the one the reader typed.
 *
 * A reader who set English titles still thinks of an anime by its romanization
 * often enough that matching only the displayed title would make search feel
 * broken. All four fields are searched; only one is rendered.
 */
function searchableTitles(show: FollowedShow): string[] {
  const { english, native, primary, romanized } = show.showTitles;
  return [primary, english, native, romanized].filter(
    (title): title is string => title !== null && title !== "",
  );
}

/**
 * Whether any of a show's titles contains the folded query.
 *
 * Substring matching, so a reader typing "ledger" finds "Star Ledger". A Set
 * answers "is this exactly a member", which is a different question and would
 * break partial search outright; the list scanned here is at most four titles.
 */
function matchesQuery(show: FollowedShow, query: string): boolean {
  // react-doctor-disable-next-line react-doctor/js-set-map-lookups
  return searchableTitles(show).some((title) => foldForSearch(title).includes(query));
}

export function filterFollowedShows(
  shows: FollowedShow[],
  filters: LibraryFilters,
): FollowedShow[] {
  const query = filters.q === undefined || filters.q === "" ? null : foldForSearch(filters.q);
  const genre =
    filters.genre === undefined || filters.genre === "" ? null : foldForSearch(filters.genre);

  return shows.filter((show) => {
    if (query !== null && !matchesQuery(show, query)) return false;
    if (filters.category !== undefined && show.category !== filters.category) return false;
    if (filters.status !== undefined && show.status !== filters.status) return false;
    if (genre !== null && !show.genres.some((candidate) => foldForSearch(candidate) === genre)) {
      return false;
    }
    if (filters.progress !== undefined && progressStateOf(show) !== filters.progress) return false;
    return true;
  });
}

export interface LibrarySortOptions {
  /** Locale-aware, because a library of Japanese and Hangul titles is not ASCII. */
  collator: Intl.Collator;
  /** The title actually on the card, so the order matches what is read. */
  titleOf: (show: FollowedShow) => string;
}

/**
 * Returns a new array; the caller's query result is Convex-owned and frozen.
 * `toSorted` rather than a spread and `sort`, for the same reason.
 *
 * Every order is total: ties fall back to the displayed title, so two shows
 * with the same year or the same progress do not swap places between renders.
 */
export function sortFollowedShows(
  shows: FollowedShow[],
  order: LibrarySortOrder,
  { collator, titleOf }: LibrarySortOptions,
): FollowedShow[] {
  const byTitle = (left: FollowedShow, right: FollowedShow) =>
    collator.compare(titleOf(left), titleOf(right));

  return shows.toSorted((left, right) => {
    if (order === "title") return byTitle(left, right);
    if (order === "year") {
      // A show with no year sorts last in either direction rather than
      // pretending to be year zero.
      const leftYear = left.year ?? Number.NEGATIVE_INFINITY;
      const rightYear = right.year ?? Number.NEGATIVE_INFINITY;
      return rightYear - leftYear || byTitle(left, right);
    }
    if (order === "progress") {
      return right.completionPercentage - left.completionPercentage || byTitle(left, right);
    }
    return right.updatedAt - left.updatedAt || byTitle(left, right);
  });
}

/**
 * The genres worth offering, drawn from the shows in hand.
 *
 * Built from the library rather than a fixed vocabulary so the control can only
 * ever offer a value that would return something.
 */
export function libraryGenreOptions(shows: FollowedShow[], collator: Intl.Collator): string[] {
  const byFolded = new Map<string, string>();
  for (const show of shows) {
    for (const genre of show.genres) {
      const key = foldForSearch(genre);
      if (key !== "" && !byFolded.has(key)) byFolded.set(key, genre);
    }
  }
  return [...byFolded.values()].toSorted((left, right) => collator.compare(left, right));
}

/** Whether anything is narrowing the list, which is what "Clear" acts on. */
export function hasActiveFilters(filters: LibraryFilters): boolean {
  return (
    (filters.q !== undefined && filters.q !== "") ||
    filters.category !== undefined ||
    filters.status !== undefined ||
    (filters.genre !== undefined && filters.genre !== "") ||
    filters.progress !== undefined
  );
}
